/**
 * Manual Review block (KL-767).
 * Figma Desktop: 11830-62724 (Section 2). Mobile: 11835-54276 (Comfort Layer).
 * Desktop: single heading left, 4 cards 284px, gap 48px, max 1280px, section padding 80px.
 * Mobile: heading center, horizontal scroll, cards 220px, gap 24px, divider + chevron nav, section padding 62px 0 62px 20px.
 * Contentful: Title = main heading; Star/Nav colour, text colour, background colour configurable.
 */

import {useRef, useCallback, useState, useEffect} from 'react';
import PropTypes from 'prop-types';

import {Box, Text} from '@salesforce/retail-react-app/app/components/shared/ui';

import {normalizeHex} from '@amblique/king-living/app/utils/normalize-hex';

const MAX_REVIEWS = 4;
const DEFAULT_STAR_COLOUR = '#1B1A18';
const DEFAULT_TEXT_COLOUR = '#1B1A18';
const DEFAULT_BG_COLOUR = '#F5F5F5';

const MOBILE_CARD_WIDTH = 220;
const MOBILE_CARD_GAP = 24;
const MOBILE_SCROLL_STEP = MOBILE_CARD_WIDTH + MOBILE_CARD_GAP;

/**
 * Returns linked review entries for display, capped at MAX_REVIEWS.
 * Contentful may emit null for draft or unresolved references when the parent is published (KINDW-1017).
 *
 * @param {{items?: unknown[]}|null|undefined} reviewsCollection - reviewsCollection from GraphQL
 * @returns {unknown[]}
 */
function getDisplayReviewItems(reviewsCollection) {
  const items = reviewsCollection?.items ?? [];
  if (!Array.isArray(items)) return [];
  return items.filter((item) => item != null).slice(0, MAX_REVIEWS);
}

/**
 * Star rating display (1–5). Figma: 16px stars, 8px gap.
 *
 * @param {number} rating - Star count
 * @param {string} starColour - Hex colour for stars
 */
function StarRating({rating, starColour}) {
  const count = Math.min(5, Math.max(0, Number(rating) || 0));
  return (
    <Box as="span" display="inline-flex" gap="8px" alignItems="center" aria-hidden>
      {Array.from({length: 5}, (_, i) => (
        <Box
          key={i}
          as="span"
          width="16px"
          height="16px"
          flexShrink={0}
          sx={{
            color: starColour,
            '& svg': {width: '100%', height: '100%', display: 'block', fill: 'currentColor'},
          }}
          aria-hidden
        >
          <StarIcon filled={i < count} />
        </Box>
      ))}
    </Box>
  );
}

StarRating.displayName = 'StarRating';
StarRating.propTypes = {
  rating: PropTypes.number,
  starColour: PropTypes.string.isRequired,
};

/** Star icon 16x16; fill uses currentColor so Contentful starColour applies. */
const STAR_PATH =
  'M8.48588 0.800001L10.7165 5.21933L15.0099 5.64467C15.2186 5.66201 15.3981 5.79917 15.4698 5.99597C15.5414 6.19277 15.492 6.41325 15.3432 6.56067L11.8099 10.0627L13.1199 14.8213C13.1746 15.0275 13.1034 15.2466 12.9378 15.3811C12.7722 15.5156 12.5431 15.5405 12.3525 15.4447L7.99988 13.2893L3.65322 15.442C3.46263 15.5378 3.23356 15.513 3.06799 15.3785C2.90241 15.244 2.83113 15.0248 2.88588 14.8187L4.19588 10.06L0.659882 6.558C0.511136 6.41058 0.461731 6.1901 0.533346 5.9933C0.604961 5.79651 0.784512 5.65934 0.993215 5.642L5.28655 5.21667L7.51388 0.800001C7.60728 0.617591 7.79495 0.502838 7.99988 0.502838C8.20481 0.502838 8.39249 0.617591 8.48588 0.800001Z';

function StarIcon({filled}) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d={STAR_PATH}
        fill="currentColor"
        fillOpacity={filled ? 1 : 0.3}
      />
    </svg>
  );
}

StarIcon.displayName = 'StarIcon';
StarIcon.propTypes = {
  filled: PropTypes.bool.isRequired,
};

/** Chevron left icon for mobile nav. Fill uses currentColor so Contentful starColour applies. */
const CHEVRON_LEFT_PATH =
  'M10.5445 19.774C10.4731 19.8457 10.3882 19.9025 10.2948 19.9413C10.2013 19.98 10.1012 20 9.99999 20C9.89882 20 9.79865 19.98 9.70521 19.9413C9.61176 19.9025 9.52689 19.8457 9.45544 19.774L0.225757 10.5443C0.15413 10.4729 0.0973014 10.388 0.0585269 10.2946C0.0197524 10.2011 -0.000206082 10.101 -0.000206073 9.9998C-0.000206064 9.89863 0.0197524 9.79846 0.0585269 9.70501C0.0973014 9.61157 0.15413 9.52669 0.225757 9.45525L9.45544 0.225561C9.59987 0.0811385 9.79575 1.82213e-06 9.99999 1.83999e-06C10.2042 1.85784e-06 10.4001 0.0811386 10.5445 0.225561C10.689 0.369986 10.7701 0.565866 10.7701 0.770113C10.7701 0.97436 10.689 1.17024 10.5445 1.31466L1.85787 9.9998L10.5445 18.6849C10.6162 18.7564 10.673 18.8413 10.7118 18.9347C10.7505 19.0281 10.7705 19.1283 10.7705 19.2295C10.7705 19.3307 10.7505 19.4308 10.7118 19.5243C10.673 19.6177 10.6162 19.7026 10.5445 19.774Z';

function ChevronLeft({color}) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="11"
      height="20"
      viewBox="0 0 11 20"
      fill="none"
      aria-hidden
      style={{color}}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d={CHEVRON_LEFT_PATH}
        fill="currentColor"
      />
    </svg>
  );
}
ChevronLeft.displayName = 'ChevronLeft';
ChevronLeft.propTypes = {color: PropTypes.string.isRequired};

/** Chevron right icon for mobile nav. Fill uses currentColor so Contentful starColour applies. */
const CHEVRON_RIGHT_PATH =
  'M0.225963 0.225964C0.29741 0.154337 0.382286 0.0975082 0.475729 0.0587336C0.569172 0.019959 0.669347 0 0.770515 0C0.871684 0 0.971859 0.019959 1.0653 0.0587336C1.15874 0.0975082 1.24362 0.154337 1.31507 0.225964L10.5448 9.45565C10.6164 9.5271 10.6732 9.61197 10.712 9.70542C10.7508 9.79886 10.7707 9.89903 10.7707 10.0002C10.7707 10.1014 10.7508 10.2015 10.712 10.295C10.6732 10.3884 10.6164 10.4733 10.5448 10.5448L1.31507 19.7744C1.17064 19.9189 0.974762 20 0.770515 20C0.566269 20 0.370388 19.9189 0.225963 19.7744C0.0815395 19.63 0.000402697 19.4341 0.000402697 19.2299C0.000402697 19.0256 0.0815395 18.8298 0.225963 18.6853L8.91264 10.0002L0.225963 1.31507C0.154336 1.24362 0.0975074 1.15875 0.0587328 1.0653C0.0199582 0.971859 0 0.871684 0 0.770516C0 0.669347 0.0199582 0.569172 0.0587328 0.475729C0.0975074 0.382286 0.154336 0.297411 0.225963 0.225964Z';

function ChevronRight({color}) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="11"
      height="20"
      viewBox="0 0 11 20"
      fill="none"
      aria-hidden
      style={{color}}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d={CHEVRON_RIGHT_PATH}
        fill="currentColor"
      />
    </svg>
  );
}
ChevronRight.displayName = 'ChevronRight';
ChevronRight.propTypes = {color: PropTypes.string.isRequired};

/** Reusable nav button for chevron left/right; supports disabled state and Contentful starColour. */
function ChevronNavButton({direction, disabled, onClick, starColour, Icon}) {
  const label = direction === 'left' ? 'Previous reviews' : 'Next reviews';
  return (
    <Box
      as="button"
      type="button"
      onClick={disabled ? undefined : onClick}
      aria-label={label}
      aria-disabled={disabled}
      disabled={disabled}
      padding={2}
      background="transparent"
      border="none"
      cursor={disabled ? 'default' : 'pointer'}
      color={starColour}
      sx={{
        opacity: disabled ? 0.4 : 1,
        '&:hover': disabled ? {} : {opacity: 0.8},
      }}
    >
      <Icon color={starColour} />
    </Box>
  );
}

ChevronNavButton.displayName = 'ChevronNavButton';
ChevronNavButton.propTypes = {
  direction: PropTypes.oneOf(['left', 'right']).isRequired,
  disabled: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  starColour: PropTypes.string.isRequired,
  Icon: PropTypes.elementType.isRequired,
};

/**
 * Single review card. Figma: Title → Stars (13px gap) → Review text.
 * Desktop: title 24px/500, body 18px/400. Mobile: title 18px/500, body 16px/400.
 */
function ReviewCard({item, starColour, textColor}) {
  if (!item || item.__typename !== 'ReviewItem') return null;
  const {title: itemTitle, heading: itemHeading, rating, description} = item;
  const displayTitle = itemTitle || itemHeading;

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="10px"
      minWidth={{base: `${MOBILE_CARD_WIDTH}px`, md: '284px'}}
      width={{base: `${MOBILE_CARD_WIDTH}px`, md: '284px'}}
      flexShrink={0}
    >
      <Box display="flex" flexDirection="column" gap="13px" flex="1">
        {displayTitle && (
          <Text
            as="h3"
            fontFamily="body"
            fontWeight={500}
            fontSize={{base: '18px', md: '24px'}}
            lineHeight={1.4}
            color={textColor}
          >
            {displayTitle}
          </Text>
        )}
        <StarRating rating={rating} starColour={starColour} />
        {description && (
          <Text
            as="p"
            fontFamily="body"
            fontWeight={400}
            fontSize={{base: '16px', md: '18px'}}
            lineHeight={{base: 1.4, md: 1.2}}
            color={textColor}
          >
            {description}
          </Text>
        )}
      </Box>
    </Box>
  );
}

ReviewCard.displayName = 'ReviewCard';
ReviewCard.propTypes = {
  item: PropTypes.object,
  starColour: PropTypes.string.isRequired,
  textColor: PropTypes.string.isRequired,
};

/**
 * Reviews (Manual Review) component
 *
 * @param {Object} props - Component props
 * @param {Contentful.Reviews} props.component - Reviews data (title, heading, reviewsCollection, starNavColour, textColour, backgroundColour)
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether data is being fetched
 * @returns {React.ReactElement|null}
 */
export const Reviews = ({
  component,
  isComplete = true,
  isFetching = false,
  ...props
}) => {
  const scrollRef = useRef(null);
  // Initial progress = first item (1/n) so on load we show e.g. 25% for 4 items, not 0%
  const [scrollProgress, setScrollProgress] = useState(() => {
    const list = getDisplayReviewItems(component?.reviewsCollection);
    const n = list.length;
    return n > 0 ? 1 / n : 1;
  });
  const [scrollState, setScrollState] = useState({isAtStart: true, isAtEnd: false});

  if (!component) return null;

  const {
    title,
    heading,
    reviewsCollection,
    starNavColour,
    textColour,
    backgroundColour,
  } = component;

  const starColour = normalizeHex(starNavColour, DEFAULT_STAR_COLOUR);
  const textColor = normalizeHex(textColour, DEFAULT_TEXT_COLOUR);
  const bgColour = normalizeHex(backgroundColour, DEFAULT_BG_COLOUR);

  const reviews = getDisplayReviewItems(reviewsCollection);
  const count = reviews.length;
  const isCenterAlign = count > 0 && count < 4;

  const scroll = useCallback((direction) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction === 'left' ? -MOBILE_SCROLL_STEP : MOBILE_SCROLL_STEP,
      behavior: 'smooth',
    });
  }, []);

  // Sync progress bar and arrow disable state with scroll position (mobile). KINDW-1019: disable arrows at start/end.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || count === 0) return;

    const updateProgress = () => {
      const {scrollLeft, scrollWidth, clientWidth} = el;
      const maxScroll = Math.max(0, scrollWidth - clientWidth);
      if (maxScroll <= 0 || count <= 1) {
        setScrollState({isAtStart: true, isAtEnd: true});
        setScrollProgress(count > 0 ? 1 / count : 1);
        return;
      }
      // Derive active card from scroll step (matches scrollBy amount). Pixel thresholds
      // avoid isAtStart stuck true on mobile (sub-pixel scrollLeft, snap, padding).
      const step = MOBILE_SCROLL_STEP;
      const edgePx = 4;
      let activeIndex = Math.round(scrollLeft / step);
      if (scrollLeft <= edgePx) activeIndex = 0;
      if (scrollLeft >= maxScroll - edgePx) activeIndex = count - 1;
      activeIndex = Math.min(count - 1, Math.max(0, activeIndex));
      setScrollState({
        isAtStart: activeIndex === 0,
        isAtEnd: activeIndex >= count - 1,
      });
      setScrollProgress((activeIndex + 1) / count);
    };

    updateProgress();
    el.addEventListener('scroll', updateProgress, {passive: true});
    const ro = new ResizeObserver(updateProgress);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateProgress);
      ro.disconnect();
    };
  }, [count]);

  if (count === 0) return null;

  return (
    <Box
      width="100%"
      backgroundColor={bgColour}
      color={textColor}
      padding={{base: '62px 0 62px 20px', md: '80px'}}
      {...props}
    >
      {/* Figma 11830-62724 Desktop: section 80px padding, content max 1280; Mobile 11835-54276: 48px 20px */}
      <Box
        width="100%"
        maxWidth="1280px"
        margin="0 auto"
        minWidth={0}
      >
        {/* Single heading (Title) or Title + subtitle (Heading). Mobile: padding-bottom 40px. */}
        <Box
          display="flex"
          flexDirection="column"
          gap={{base: '10px', md: '24px'}}
          mb={{base: 0, md: '48px'}}
          paddingBottom={{base: '40px', md: 0}}
        >
          {title && (
            <Text
              as="h2"
              fontFamily="heading"
              fontWeight={{base: 400, md: 500}}
              fontSize={{base: '24px', md: '32px'}}
              lineHeight={{base: 1.1, md: 1.4}}
              textAlign={{base: 'center', md: 'left'}}
              color={textColor}
            >
              {title}
            </Text>
          )}
          {heading && title !== heading && (
            <Text
              as="p"
              fontFamily="body"
              fontSize={{base: 'md', md: 'lg'}}
              textAlign={{base: 'center', md: 'left'}}
              color={textColor}
            >
              {heading}
            </Text>
          )}
        </Box>

        {/* Desktop: row of 4 cards, gap 48px, center when < 4. Figma: single row, no wrap. */}
        <Box
          display={{base: 'none', md: 'flex'}}
          flexDirection="row"
          gap="48px"
          justifyContent={isCenterAlign ? 'center' : 'flex-start'}
          flexWrap="nowrap"
        >
          {reviews.map((item, index) => (
            <ReviewCard
              key={item?.sys?.id || item?.title || `review-${index}`}
              item={item}
              starColour={starColour}
              textColor={textColor}
            />
          ))}
        </Box>

        {/* Mobile: Figma 11835-54276. No right padding; 48px gap list–divider; 48px bottom on chevron. */}
        <Box display={{base: 'block', md: 'none'}}>
          <Box
            ref={scrollRef}
            overflowX="auto"
            overflowY="hidden"
            display="flex"
            flexDirection="row"
            gap={`${MOBILE_CARD_GAP}px`}
            padding="48px 0 0 20px"
            marginBottom="48px"
            justifyContent={count === 1 ? 'center' : 'flex-start'}
            sx={{
              scrollSnapType: 'x mandatory',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {display: 'none'},
              '& > *': {scrollSnapAlign: 'start'},
            }}
          >
            {reviews.map((item, index) => (
              <ReviewCard
                key={item?.sys?.id || item?.title || `review-${index}`}
                item={item}
                starColour={starColour}
                textColor={textColor}
              />
            ))}
          </Box>

          {/* Divider + chevron nav: only show when more than one review (KINDW-1018). */}
          {count > 1 && (
            <>
              {/* Divider / progress bar: track starColour 30% (KINDW-1019); filled part uses starColour (KINDW-1016). */}
              <Box
                width="100%"
                height="3px"
                backgroundColor={`${starColour}4D`}
                position="relative"
                sx={{
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: `${scrollProgress * 100}%`,
                    height: '3px',
                    backgroundColor: starColour,
                    transition: 'width 0.15s ease-out',
                  },
                }}
                aria-hidden
              />

              {/* Chevron nav: padding 0 0 48px (Figma). Disable at start/end (KINDW-1019). */}
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="center"
                alignItems="center"
                gap="48px"
                padding="48px 0 0"
              >
                <ChevronNavButton
                  direction="left"
                  disabled={scrollState.isAtStart}
                  onClick={() => scroll('left')}
                  starColour={starColour}
                  Icon={ChevronLeft}
                />
                <ChevronNavButton
                  direction="right"
                  disabled={scrollState.isAtEnd}
                  onClick={() => scroll('right')}
                  starColour={starColour}
                  Icon={ChevronRight}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

Reviews.displayName = 'Reviews';
Reviews.propTypes = {
  component: PropTypes.shape({
    title: PropTypes.string,
    heading: PropTypes.string,
    reviewsCollection: PropTypes.shape({
      items: PropTypes.arrayOf(
        PropTypes.shape({
          __typename: PropTypes.string,
          sys: PropTypes.shape({id: PropTypes.string}),
          title: PropTypes.string,
          heading: PropTypes.string,
          rating: PropTypes.number,
          description: PropTypes.string,
        })
      ),
    }),
    starNavColour: PropTypes.string,
    textColour: PropTypes.string,
    backgroundColour: PropTypes.string,
  }),
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
